import {
  Company_User_Role,
  CompanyUsersDocument,
  CompanyUserStatisticDocument,
  InviteCompanyUserInput,
  useInviteCompanyUserMutation,
} from "@generated/graphql";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import Form from "components/form/Form";
import SelectControl from "components/form/SelectControl";
import TextFieldControl from "components/form/TextFieldControl";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import VALIDATION from "helpers/validation";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import TEAM_ROLE_OPTIONS from "../constants";

const COMMON_SX: SxProps = {
  px: 3,
};

type InviteEmployeeModalProps = {
  open: boolean;
  onClose: () => void;
};

type InviteEmployeeFormInputs = Omit<InviteCompanyUserInput, "role"> & {
  role: Company_User_Role | "";
};

const InviteEmployeeModal = ({ open, onClose }: InviteEmployeeModalProps) => {
  const { addToast } = useContext(ToastContext);
  const form = useForm<InviteEmployeeFormInputs>({
    defaultValues: {
      email: "",
      position: "",
      role: "",
    },
  });
  const { handleSubmit, reset, watch } = form;
  const selectedRole = TEAM_ROLE_OPTIONS.find((role) => role.value === watch("role"));
  const [inviteCompanyUser, { loading }] = useInviteCompanyUserMutation();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (formData: InviteEmployeeFormInputs) => {
    const { role, ...inviteData } = formData;
    if (!role) return;

    inviteCompanyUser({
      variables: {
        input: {
          ...inviteData,
          role,
        },
      },
      refetchQueries: [CompanyUserStatisticDocument, CompanyUsersDocument],
      awaitRefetchQueries: true,
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: `Приглашение отправлено на ${formData.email}` });
        handleClose();
      })
      .catch((error) => parseApolloError(error, { addToast }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      sx={{ "& .MuiPaper-root": { px: 0 } }}>
      <Form
        form={form}
        onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
          sx={{ ...COMMON_SX }}>
          <Stack spacing={1}>
            <DialogTitle
              variant='h24'
              fontWeight={500}
              sx={{ p: 0 }}>
              Пригласить сотрудника
            </DialogTitle>
            <Typography color='text.secondary'>На email придет ссылка активации на 7 дней</Typography>
          </Stack>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ mt: 2, mb: 2.5 }} />
        <DialogContent sx={{ ...COMMON_SX }}>
          <Stack spacing={2.5}>
            <TextFieldControl
              name='email'
              label='Email'
              type='email'
              placeholder='name@company.ru'
              variant='outlined'
              rules={{ required: true, ...VALIDATION.email }}
            />
            <TextFieldControl
              name='position'
              label='Должность'
              placeholder='HR-менеджер'
              variant='outlined'
              rules={{ required: true, ...VALIDATION.position }}
            />
            <SelectControl
              name='role'
              label='Роль'
              placeholder='Выберите роль'
              items={TEAM_ROLE_OPTIONS}
              valueName='value'
              labelName='label'
              variant='outlined'
              sx={{ backgroundColor: "bgSwitch" }}
              hint={selectedRole?.hint}
              rules={{ required: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ ...COMMON_SX }}>
          <Button
            variant='outlined'
            onClick={handleClose}>
            Отменить
          </Button>
          <LoadingButton
            variant='contained'
            type='submit'
            loading={loading}>
            Отправить приглашение
          </LoadingButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default InviteEmployeeModal;
